import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Play, 
  Video, 
  Users, 
  Calendar, 
  Settings, 
  Monitor,
  Smartphone,
  Tablet,
  Clock,
  CheckCircle2,
  ArrowRight,
  Zap
} from 'lucide-react';

const LiveStreamDemo = () => {
  const [activeDemo, setActiveDemo] = useState('setup');

  const demoSteps = [
    {
      id: 'setup',
      title: 'Quick Setup',
      duration: '2 minutes',
      icon: Settings,
      description: 'Configure your event in under 2 minutes',
      features: [
        'Event name and description',
        'Date and time settings', 
        'Viewer access controls',
        'Payment integration setup'
      ]
    },
    {
      id: 'stream',
      title: 'Go Live',
      duration: '1 click',
      icon: Video,
      description: 'Start streaming with a single click',
      features: [
        'Automatic HD quality detection',
        'Real-time viewer counter',
        'Live chat moderation',
        'Multi-camera switching'
      ]
    },
    {
      id: 'viewers',
      title: 'Family Experience', 
      duration: 'Seamless',
      icon: Users,
      description: 'Families enjoy premium viewing experience',
      features: [
        'Works on any device',
        'No app downloads required',
        'Adaptive quality streaming',
        'Instant replay features'
      ]
    },
    {
      id: 'recording',
      title: 'Auto Recording',
      duration: 'Instant',
      icon: Clock,
      description: 'Recordings available immediately',
      features: [
        'Automatic cloud recording',
        'HD download links',
        'Shareable family links',
        '30-90 day access'
      ]
    }
  ];

  const deviceCompatibility = [
    { icon: Monitor, name: 'Desktop', compatibility: '100%' },
    { icon: Tablet, name: 'Tablet', compatibility: '100%' },
    { icon: Smartphone, name: 'Mobile', compatibility: '100%' },
    { icon: Video, name: 'Smart TV', compatibility: '95%' }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <Badge className="bg-gradient-to-r from-green-500 to-emerald-500 text-white mb-6 px-6 py-2 text-lg">
            <Play className="h-5 w-5 mr-2" />
            See It In Action
          </Badge>
          <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            From Setup to Stream in
            <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent"> Under 10 Minutes</span>
          </h2>
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto">
            Watch how easy it is to transform your next event into a professional live streaming experience that families will never forget.
          </p>
        </motion.div>

        {/* Interactive Demo */}
        <div className="max-w-7xl mx-auto mb-20">
          {/* Demo Video Placeholder */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="relative bg-gradient-to-br from-gray-900 to-gray-700 rounded-3xl shadow-2xl aspect-video mb-12 overflow-hidden"
          >
            {/* Video Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            
            {/* Play Button */}
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white/90 hover:bg-white rounded-full p-8 shadow-2xl group transition-all duration-300"
              >
                <Play className="h-16 w-16 text-blue-600 ml-2 group-hover:text-blue-700" />
              </motion.button>
            </div>
            
            {/* Demo Labels */}
            <div className="absolute top-6 left-6">
              <Badge className="bg-red-500 text-white animate-pulse">
                🔴 LIVE DEMO
              </Badge>
            </div>
            
            <div className="absolute bottom-6 left-6 right-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <h3 className="text-white text-2xl font-bold mb-2">Elite Dance Academy - Spring Recital</h3>
                <div className="flex items-center gap-6 text-white/80">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    <span>247 viewers</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    <span>1:23:45 elapsed</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Video className="h-5 w-5" />
                    <span>HD Quality</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Demo Steps */}
          <div className="grid lg:grid-cols-4 gap-6">
            {demoSteps.map((step, index) => (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="cursor-pointer"
                onClick={() => setActiveDemo(step.id)}
              >
                <Card className={`h-full transition-all duration-300 ${
                  activeDemo === step.id 
                    ? 'border-2 border-blue-300 shadow-lg scale-105 bg-blue-50' 
                    : 'border border-gray-200 hover:shadow-md hover:border-blue-100'
                }`}>
                  <CardContent className="p-6 text-center">
                    <div className={`rounded-2xl p-4 w-fit mx-auto mb-4 ${
                      activeDemo === step.id 
                        ? 'bg-blue-100' 
                        : 'bg-gray-100'
                    }`}>
                      <step.icon className={`h-8 w-8 ${
                        activeDemo === step.id ? 'text-blue-600' : 'text-gray-600'
                      }`} />
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                    <Badge className={`mb-3 ${
                      activeDemo === step.id 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {step.duration}
                    </Badge>
                    <p className="text-gray-600 text-sm mb-4">{step.description}</p>
                    
                    <ul className="space-y-2">
                      {step.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center text-xs text-gray-600">
                          <CheckCircle2 className="h-3 w-3 text-green-500 mr-2 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Device Compatibility */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-20"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Works on Every Device
            </h3>
            <p className="text-xl text-gray-600">
              Families can watch from anywhere, on any device - no apps required
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {deviceCompatibility.map((device, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <Card className="hover:shadow-lg transition-all duration-300 group">
                  <CardContent className="p-6">
                    <div className="bg-gradient-to-r from-blue-100 to-purple-100 rounded-xl p-4 w-fit mx-auto mb-4 group-hover:from-blue-200 group-hover:to-purple-200 transition-colors">
                      <device.icon className="h-8 w-8 text-blue-600" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">{device.name}</h4>
                    <div className="text-2xl font-bold text-green-600 mb-1">{device.compatibility}</div>
                    <div className="text-sm text-gray-500">Compatible</div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-12 text-white"
        >
          <h3 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to See It Live?
          </h3>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Book a personalized demo and we'll show you exactly how HiveShare Live can transform your next event.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              size="lg"
              className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 text-lg font-bold shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <Calendar className="h-5 w-5 mr-2" />
              Schedule Live Demo
            </Button>
            
            <Button
              variant="outline"
              size="lg"
              className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 text-lg font-bold"
            >
              <Zap className="h-5 w-5 mr-2" />
              Start Free Trial
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default LiveStreamDemo;